/**
 * Переходы по ссылкам на каналы и сообщения внутри приложения: клик по ссылке в сообщении,
 * адрес, с которым открыли Кличат. В приложении для компьютера новые окна запрещены и уходят
 * в системный браузер — поэтому свои ссылки перехватываем и открываем здесь же.
 */
import { parseAppLink, type ChannelLink } from '@/lib/appLinks'
import { P, channelPermissions, has, threadPermissions } from '@/lib/permissions'
import { socialApi } from '@/lib/socialApi'
import { useAppStore } from '@/store/app'
import { useGuildStore } from '@/store/guilds'
import { useSocialStore } from '@/store/social'
import { ChannelType, type Channel, type Guild, type Member } from '@/types/api'

/** Участник «я» на сервере; пока список участников не пришёл — без ролей, как в списке каналов. */
function selfIn(guildId: string): Member | null {
  const me = useAppStore.getState().currentUser
  if (!me) return null
  return (
    useGuildStore.getState().members[guildId]?.[me.id] ?? {
      user: { id: me.id, username: me.username, global_name: me.global_name, avatar: null },
      nick: null, avatar: null, roles: [], joined_at: '', premium_since: null, deaf: false, mute: false, pending: false, communication_disabled_until: null,
    }
  )
}

function canView(guild: Guild, ch: Channel, channels: Record<string, Channel> | undefined): boolean {
  const self = selfIn(guild.id)
  if (!self) return false
  const perms = ch.type === ChannelType.PublicThread ? threadPermissions(guild, self, ch.parent_id ? channels?.[ch.parent_id] : undefined) : channelPermissions(guild, self, ch)
  return has(perms, P.VIEW_CHANNEL)
}

/** Канал по ссылке, если он виден: из сторов, а архивную ветку или закрытую беседу — с сервера. */
async function resolve(link: ChannelLink): Promise<boolean> {
  if (!link.guildId) {
    if (useSocialStore.getState().channels[link.channelId]) return true
    try {
      // закрытая беседа: получатель её видит, и она возвращается в список, как у Discord
      const ch = await socialApi.channel(link.channelId)
      if (ch.guild_id) return false
      useSocialStore.setState((s) => ({ channels: { ...s.channels, [ch.id]: ch } }))
      return true
    } catch {
      return false
    }
  }
  const store = useGuildStore.getState()
  const guild = store.guilds[link.guildId]
  if (!guild) return false
  let ch = store.channels[link.guildId]?.[link.channelId]
  if (!ch) {
    try {
      // архивных веток в READY нет — спросим сервер, он же проверит права
      const fetched = await socialApi.channel(link.channelId)
      if (fetched.guild_id !== link.guildId) return false
      store.applyEvent(fetched.type === ChannelType.PublicThread ? 'THREAD_UPDATE' : 'CHANNEL_UPDATE', fetched)
      ch = fetched
    } catch {
      return false
    }
  }
  if (ch.type === ChannelType.GuildCategory) return false
  return canView(guild, ch, useGuildStore.getState().channels[link.guildId])
}

/**
 * Открыть канал по ссылке, а если в ней есть сообщение — прокрутить к нему. Нет доступа
 * (не участник сервера, канал скрыт или удалён) — окно «Нет доступа к этому каналу».
 */
export async function openChannelLink(link: ChannelLink): Promise<boolean> {
  const app = useAppStore.getState()
  if (!(await resolve(link))) {
    app.setLinkDenied(true)
    return false
  }
  if (link.guildId) {
    app.openGuild(link.guildId, link.channelId)
    if (useGuildStore.getState().channels[link.guildId]?.[link.channelId]?.type === ChannelType.GuildVoice) {
      app.setVoiceChatChannelId(link.channelId)
      app.setSearchQuery(null)
    }
  }
  else app.setView({ kind: 'home', channelId: link.channelId })
  if (link.messageId) app.setJumpTarget({ channelId: link.channelId, messageId: link.messageId })
  return true
}

/**
 * Клик по ссылке на Кличат (канал, сообщение, приглашение) открывает её здесь же, а не в новой
 * вкладке. С Ctrl, Cmd или Shift и средней кнопкой браузер делает своё — как и ждёт человек.
 */
export function interceptAppLinks(): () => void {
  const onClick = (e: MouseEvent) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    const a = (e.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null
    if (!a || a.hasAttribute('download')) return
    const link = parseAppLink(a.href, location.origin)
    if (!link) return
    e.preventDefault()
    if (link.kind === 'invite') useAppStore.getState().setPendingInvite(link.code)
    else void openChannelLink(link)
  }
  document.addEventListener('click', onClick)
  return () => document.removeEventListener('click', onClick)
}
